import { EmptyState, cx } from '@/components/ui';
import {
  isMarketReady,
  marketScopeLabel,
  topicParticle,
  useMarketScope,
  type MarketScope,
} from './marketScope';

const SCOPES: MarketScope[] = ['KR', 'US'];

/**
 * 국내 / 미국 스위치. (2026-09-19, I-53 · 같은 날 저녁 위치와 모양을 바꿨다)
 *
 * 🔴 하위 탭 위에 둔다 - TradingNav 가 그린다 (사용자 결정)
 *   처음에는 시장별 화면마다 하위 탭 아래 줄에 따로 그렸다. 그러면 "AI 모의투자 안의 탭" 처럼 읽혔다.
 *   시장을 먼저 고르고 그 안에서 화면을 고르는 순서가 눈에 보이게 위로 올렸다.
 *
 * 🔴 알약(탭)이 아니라 스위치 모양이다
 *   바로 아래 하위 탭이 알약 모양이라 같은 모양이면 탭이 한 줄 더 있는 것처럼 보인다.
 *   "둘 중 하나를 고른다" 는 뜻이 보이게 테두리 안의 두 칸으로 그린다(radiogroup).
 *
 * 🔴 선택은 화면끼리 공유된다
 *   추천에서 해외를 고르고 모의투자로 넘어가면 거기도 해외다. 스토어가 localStorage 에 남긴다.
 *
 * 🔴 공통 화면에서도 누를 수 있다 (2026-09-20 사용자 지시)
 *   전에는 공통 화면(가격 알림 · 환율 — 지금은 시장지표)에서 스위치를 disabled 로 막고 흐리게 뒀다.
 *   그러면 환율을 보다가 미국으로 넘어가려면 다른 탭을 먼저 눌러야 한다 -
 *   사용자 말대로 "전환은 되되, 공통인 것은 그대로 유지되고 아래 탭 구성만 바뀐다" 가 맞다.
 *   그래서 막지 않고, 대신 "내용이 같다" 는 사실만 옆에 적는다.
 *
 * @param common   공통 화면(환율 — 가격 알림은 2026-09-28 부터 스위치를 따른다)이면 "내용은 같다" 를 옆에 적는다. 스위치는 그대로 눌린다
 * @param onChange 바꾼 뒤 부른다. TradingNav 가 국내 전용 화면에서 미국으로 바꾸면 추천으로 옮긴다
 */
export function MarketSwitch({
  common = false,
  onChange,
}: {
  common?: boolean;
  onChange?: (next: MarketScope) => void;
}) {
  const { scope, setScope } = useMarketScope();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="radiogroup"
        aria-label="시장"
        className="inline-flex rounded-lg border border-line bg-surface-hover p-0.5"
      >
        {SCOPES.map((it) => (
          <button
            key={it}
            type="button"
            role="radio"
            aria-checked={scope === it}
            onClick={() => {
              setScope(it);
              onChange?.(it);
            }}
            className={cx(
              'inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-4 text-sm font-medium transition-colors',
              // 선택된 칸은 테두리까지 - 다크 모드에서 배경 차이만으로는 약했다
              scope === it ? 'bg-surface text-fg shadow-sm ring-1 ring-line-strong' : 'text-fg-muted hover:text-fg',
            )}
          >
            <MarketFlag scope={it} />
            {marketScopeLabel(it)}
          </button>
        ))}
      </div>
      {/*
        🔴 폰에서 이 문장이 가로로 넘쳤다 (2026-09-20 미리보기에서 잡았다)
          flex 아이템의 기본 min-width 는 auto 라 긴 글이 줄바꿈하지 않고 줄을 밀어낸다.
          400px 에서 문장이 화면 밖으로 나가 단타 화면 전체에 가로 스크롤이 생겼다.
          w-full 로 폰에서는 한 줄을 통째로 쓰게 해 글이 접히게 하고,
          넓은 화면(sm 이상)에서는 예전처럼 스위치 옆에 붙인다.
      */}
      {common && (
        <span className="w-full text-xs text-fg-subtle sm:w-auto">
          이 화면은 국내 · 미국 공통입니다 — 전환해도 내용은 같고 아래 탭만 바뀝니다
        </span>
      )}
    </div>
  );
}

/**
 * 시장 앞에 붙는 작은 국기. (2026-09-20 사용자 요청)
 *
 * 🔴 유니코드 국기 이모지(🇰🇷 🇺🇸)를 쓰지 않는다
 *   윈도우는 국기 글리프가 없어 Chrome·Edge 에서 `KR` · `US` 두 글자로 보인다.
 *   폰(안드로이드·iOS)에서만 국기로 보이므로 기기마다 다른 화면이 된다.
 *   그래서 글자 크기(1em)에 맞춘 인라인 SVG 로 그린다 - 어디서나 같다.
 *
 * 세밀한 국기가 아니다. 14px 안에서는 태극의 괘 3줄이나 성조기의 별 50개가 뭉개지므로
 * 알아볼 수 있는 최소 요소만 남겼다 - 태극은 원과 네 모서리 선, 성조기는 줄무늬와 파란 칸.
 */
export function MarketFlag({ scope }: { scope: MarketScope }) {
  const common = 'h-[1em] w-[1.5em] shrink-0 rounded-[1px] ring-1 ring-line';
  if (scope === 'US') {
    return (
      <svg viewBox="0 0 36 24" className={common} aria-hidden="true">
        <rect width="36" height="24" fill="#fff" />
        {[0, 2, 4, 6, 8, 10].map((i) => (
          <rect key={i} y={i * 2 + 1.85} width="36" height="1.85" fill="#b31942" />
        ))}
        <rect width="15" height="13" fill="#0a3161" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 36 24" className={common} aria-hidden="true">
      <rect width="36" height="24" fill="#fff" />
      <circle cx="18" cy="12" r="5.5" fill="#0047a0" />
      {/* 태극의 붉은 반쪽. 반원 두 개를 S 로 이어 붙인 표준 도형 */}
      <path d="M18 6.5a2.75 2.75 0 0 1 0 5.5a2.75 2.75 0 0 0 0 5.5a5.5 5.5 0 0 0 0-11" fill="#cd2e3a" />
      {/* 네 모서리의 괘 - 3줄을 1줄로 줄였다 */}
      <g stroke="#000" strokeWidth="1.3">
        <path d="M5 5.5l4.5 4.5M5 18.5l4.5-4.5M31 5.5l-4.5 4.5M31 18.5l-4.5-4.5" />
      </g>
    </svg>
  );
}

/**
 * 아직 계좌·추천이 없는 시장에서 보여줄 안내. (2026-09-19 · 2026-09-20 고침)
 *
 * 🔴 "결과 없음" 과 가려야 한다
 *   빈 목록을 보여주면 사용자는 "오늘은 후보가 없구나" 로 읽는다 —
 *   실제로는 아직 만들지 않은 것이다. 둘은 전혀 다른 상태다.
 *
 * 🔴 호출부는 이것이 있으면 **그것만 그리고 끝내야 한다** (2026-09-20 사용자 발견)
 *   원래 주석에 그렇게 적혀 있었는데 실제 호출부 다섯 곳은 이 안내를 **배너로 위에 얹고
 *   국내 데이터를 그대로 아래에 계속 그렸다.** 그래서 미국을 고른 채 AI 모의투자를 열면
 *   "준비 중" 과 국내 계좌 6개가 동시에 보였다. 사용자가 그것을 보고 지적했다.
 *   이제 모든 호출부가 early return 한다 - 이 부품을 새 화면에 붙일 때도 그래야 한다.
 *
 * @returns 준비된 시장이면 null
 */
/**
 * 🔴 reason 을 화면마다 다르게 받는다 (2026-09-20)
 *
 * 이 부품을 쓰는 화면이 넷인데 막힌 이유가 같지 않다.
 *   지난 추천              추천을 아직 안 붙였다 (기본 문구)
 *                          ✏️ 9/28 — AI 모의투자는 미국 저점매수 계좌(V141)가 생겨 PaperPage 가 이 부품을 거치지 않는다
 *   주도주 · 섹터           td_sector_map 에 US 행이 0 이다. 계좌와 무관하다
 *
 * 기본 문구를 그대로 쓰면 섹터 탭에서 "계좌가 붙으면 섹터도 보인다" 로 읽힌다.
 * 실제로는 섹터 분류를 손으로 넣어야 하는 별개의 일이다.
 */
export function MarketNotReady({ reason }: { reason?: string } = {}) {
  const { scope } = useMarketScope();
  if (isMarketReady(scope)) {
    return null;
  }
  return (
    <EmptyState
      title={`${marketScopeLabel(scope)}${topicParticle(marketScopeLabel(scope))} 준비 중입니다`}
      description={
        reason ??
        // 🔴 "일봉은 받아 두었습니다" 가 과했다 - 마스터 10,662종목 중 거래가 활발한 일부만이다
        // ✏️ 9/28 I-78 2차 — "추천과 계좌를 붙이는 중" 이었다. 계좌(미국 저점매수 모의)는 붙었다 — 쓸 수 있는 목록에 넣는다
        '종목 목록은 받아 두었고 일봉은 거래가 활발한 일부만 받아 두었습니다. 미국 추천은 4회차부터 매일 오전 10:45 에 골라 [AI 모의투자]의 🇺🇸 미국 추천 탭에서 사고팔지만, 이 화면에는 아직 붙이지 않아 비어 있습니다. 검색과 종목 상세, 관심종목, 가격 알림, AI 모의투자(미국 저점매수 · 미국 스윙 · 미국 추천 — 4회차부터 매수)는 지금도 쓸 수 있습니다.'
      }
    />
  );
}
